import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiPost } from '../utils/apiClient';

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const urlToken = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [token, setToken] = useState(() => urlToken || localStorage.getItem('mp:pendingInviteToken') || '');

  // Redirect old accept-invite links to the new invitation flow
  useEffect(() => {
    if (urlToken) {
      navigate(`/invite?type=household&token=${encodeURIComponent(urlToken)}`, { replace: true });
    }
  }, [urlToken, navigate]);

  // Keep token in sync with URL and stash for post-login acceptance
  useEffect(() => {
    if (urlToken && urlToken !== token) {
      setToken(urlToken);
      localStorage.setItem('mp:pendingInviteToken', urlToken);
    }
  }, [urlToken, token]);

  useEffect(() => {
    if (!token || !user || status !== 'idle') return;
    let cancelled = false;
    (async () => {
      setStatus('loading');
      try {
        await apiPost(`/households/invitations/${encodeURIComponent(token)}/accept`);
        if (cancelled) return;
        toast.success('Invitation accepted');
        setStatus('success');
        localStorage.removeItem('mp:pendingInviteToken');
        navigate('/family', { replace: true });
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || 'Could not accept invitation';
        toast.error(msg);
        setErrorMsg(msg);
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, user, status, navigate]);

  const missingToken = !token;
  const needsLogin = token && !user;

  useEffect(() => {
    if (!needsLogin || !token) return;
    localStorage.setItem('mp:pendingInviteToken', token);
  }, [needsLogin, token]);

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
      <Toaster position="top-center" richColors closeButton />
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm p-6 text-center">
        <h1 className="text-xl mb-2">Join Household</h1>
        {missingToken && <p className="text-[#757575]">Invitation token is missing or invalid.</p>}
        {needsLogin && (
          <>
            <p className="text-[#757575] mb-4">Sign in to accept this invitation.</p>
            <button
              onClick={() => navigate('/auth/signin', { replace: false })}
              className="w-full py-3 bg-[#AFBB8B] text-white rounded-full hover:bg-[#9FAA7B] transition-colors"
            >
              Sign in
            </button>
          </>
        )}
        {!needsLogin && !missingToken && status === 'loading' && (
          <p className="text-[#757575]">Accepting your invitation...</p>
        )}
        {!needsLogin && !missingToken && status === 'error' && (
          <div className="space-y-3">
            <p className="text-[#B85A5A]">{errorMsg || 'Could not accept the invitation. Try again or request a new link.'}</p>
            <button
              onClick={() => setStatus('idle')}
              className="w-full py-3 bg-[#AFBB8B] text-white rounded-full hover:bg-[#9FAA7B] transition-colors"
            >
              Try Again
            </button>
          </div>
        )}
        {!needsLogin && !missingToken && status === 'success' && (
          <p className="text-[#6B8A78]">Invitation accepted. Redirecting...</p>
        )}
      </div>
    </div>
  );
}

export default AcceptInvitePage;